/**
 * DebtorFormModal — Add/Edit a Sundry Debtor (or Both).
 *
 * Extracted from Debtors.jsx (iter200) to keep the page <600 lines.
 * All state is owned by the parent — this component is a controlled view.
 */
import React from 'react';
import { X, BookOpen, Truck } from 'lucide-react';
import AIMemoryBanner from '../AIMemoryBanner';

export default function DebtorFormModal({
  open,
  editing,
  form,
  setForm,
  submitting,
  onClose,
  onSubmit,
}) {
  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/70 flex items-start justify-center z-[60] p-4 overflow-y-auto" data-testid="debtor-form-modal">
      <div className="bg-zinc-900 border border-zinc-700 rounded-2xl w-full max-w-lg my-6">
        <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-800">
          <h3 className="font-heading font-semibold text-zinc-50">{editing ? 'Edit Debtor' : 'New Sundry Debtor'}</h3>
          <button onClick={onClose} data-testid="debtor-form-close"><X size={18} className="text-zinc-500 hover:text-zinc-200" /></button>
        </div>
        <form onSubmit={onSubmit} className="p-5 space-y-4" data-testid="debtor-form">
          {editing && form.name && <AIMemoryBanner client={form.name} />}
          <div>
            <label className="text-zinc-400 text-xs mb-1.5 block">Client Type</label>
            <div className="flex gap-2">
              {['Debtor', 'Both'].map(t => (
                <button key={t} type="button"
                  onClick={() => setForm(f => ({ ...f, type: t }))}
                  className={`flex-1 py-2 rounded-xl border text-xs font-semibold transition-all ${form.type === t ? (t === 'Both' ? 'bg-amber-900/40 text-amber-300 border-amber-700' : 'bg-blue-900/40 text-blue-300 border-blue-700') : 'border-zinc-700 text-zinc-400 hover:border-zinc-600'}`}>
                  {t === 'Both' ? 'Debtor + Creditor' : 'Sundry Debtor'}
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <label className="text-zinc-400 text-xs mb-1.5 block">Company / Client Name *</label>
              <input required value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                placeholder="e.g. BigBasket Ltd." data-testid="debtor-name-input"
                className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2.5 text-zinc-50 text-sm focus:outline-none focus:border-amber-500" />
            </div>
            <div>
              <label className="text-zinc-400 text-xs mb-1.5 block">GSTIN</label>
              <input value={form.gstin} onChange={e => setForm(f => ({ ...f, gstin: e.target.value }))}
                placeholder="27AABCX..." className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2.5 text-zinc-50 text-sm focus:outline-none focus:border-amber-500" />
            </div>
            <div>
              <label className="text-zinc-400 text-xs mb-1.5 block">Phone</label>
              <input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                placeholder="+91 9876543210" className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2.5 text-zinc-50 text-sm focus:outline-none focus:border-amber-500" />
            </div>
            <div>
              <label className="text-zinc-400 text-xs mb-1.5 block">Email</label>
              <input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                placeholder="accounts@client.com" className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2.5 text-zinc-50 text-sm focus:outline-none focus:border-amber-500" />
            </div>
            <div>
              <label className="text-zinc-400 text-xs mb-1.5 block">City</label>
              <input value={form.city} onChange={e => setForm(f => ({ ...f, city: e.target.value }))}
                placeholder="Mumbai" className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2.5 text-zinc-50 text-sm focus:outline-none focus:border-amber-500" />
            </div>
            <div>
              <label className="text-zinc-400 text-xs mb-1.5 block">State</label>
              <input value={form.state} onChange={e => setForm(f => ({ ...f, state: e.target.value }))}
                className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2.5 text-zinc-50 text-sm focus:outline-none focus:border-amber-500" />
            </div>
            <div className="col-span-2">
              <label className="text-zinc-400 text-xs mb-1.5 block">Address</label>
              <input value={form.address} onChange={e => setForm(f => ({ ...f, address: e.target.value }))}
                placeholder="Full address" className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2.5 text-zinc-50 text-sm focus:outline-none focus:border-amber-500" />
            </div>
            <div>
              <label className="text-zinc-400 text-xs mb-1.5 block">Payment Terms</label>
              <select value={form.payment_terms} onChange={e => setForm(f => ({ ...f, payment_terms: e.target.value }))}
                className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2.5 text-zinc-50 text-sm focus:outline-none focus:border-amber-500">
                {['Immediate', 'Net 7', 'Net 15', 'Net 30', 'Net 45', 'Net 60', 'Advance'].map(t => (
                  <option key={t} value={t} className="bg-zinc-900">{t}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-zinc-400 text-xs mb-1.5 block">Credit Limit (₹)</label>
              <input type="number" min="0" value={form.credit_limit} onChange={e => setForm(f => ({ ...f, credit_limit: e.target.value }))}
                placeholder="0" className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2.5 text-zinc-50 text-sm focus:outline-none focus:border-amber-500" />
            </div>
            {/* ── Opening Balance (FY carry-forward) ────────────────────── */}
            <div className="col-span-2 mt-2 p-3 rounded-xl bg-amber-500/5 border border-amber-500/20">
              <div className="text-amber-400 text-xs font-semibold mb-2 flex items-center gap-1.5">
                <BookOpen size={12} /> Opening Balance — Carry forward from previous FY
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <div>
                  <label className="text-zinc-400 text-[10px] mb-1 block">Amount (₹)</label>
                  <input type="number" min="0" step="any" value={form.opening_balance}
                    data-testid="opening-balance-input"
                    onChange={e => setForm(f => ({ ...f, opening_balance: e.target.value }))}
                    placeholder="0"
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-2.5 py-2 text-zinc-50 text-sm focus:outline-none focus:border-amber-500" />
                </div>
                <div>
                  <label className="text-zinc-400 text-[10px] mb-1 block">Type</label>
                  <select value={form.opening_balance_type}
                    data-testid="opening-balance-type"
                    onChange={e => setForm(f => ({ ...f, opening_balance_type: e.target.value }))}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-2.5 py-2 text-zinc-50 text-sm focus:outline-none focus:border-amber-500">
                    <option value="Dr" className="bg-zinc-900">Dr (owes us)</option>
                    <option value="Cr" className="bg-zinc-900">Cr (advance)</option>
                  </select>
                </div>
                <div>
                  <label className="text-zinc-400 text-[10px] mb-1 block">As of</label>
                  <input type="date" value={form.opening_balance_as_of}
                    data-testid="opening-balance-date"
                    onChange={e => setForm(f => ({ ...f, opening_balance_as_of: e.target.value }))}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-2.5 py-2 text-zinc-50 text-sm focus:outline-none focus:border-amber-500" />
                </div>
              </div>
              <input value={form.opening_balance_note || ''}
                onChange={e => setForm(f => ({ ...f, opening_balance_note: e.target.value }))}
                placeholder="Note (e.g. 'As per audited books FY 2025-26')"
                className="w-full mt-2 bg-zinc-800 border border-zinc-700 rounded-lg px-2.5 py-2 text-zinc-300 text-xs focus:outline-none focus:border-amber-500" />
            </div>
            {/* ── Default Dispatch & Reference details (auto-filled into bills) ────── */}
            <div className="col-span-2 mt-2 p-3 rounded-xl bg-blue-500/5 border border-blue-500/20" data-testid="dispatch-defaults-section">
              <div className="text-blue-400 text-xs font-semibold mb-2 flex items-center gap-1.5">
                <Truck size={12} /> Default Dispatch & Reference (auto-filled into Enterprise / GST bill)
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="col-span-2">
                  <label className="text-zinc-400 text-[10px] mb-1 block">Dispatch address (if different from billing)</label>
                  <input value={form.default_dispatch_address || ''}
                    data-testid="default-dispatch-address"
                    onChange={e => setForm(f => ({ ...f, default_dispatch_address: e.target.value }))}
                    placeholder="Leave blank to reuse billing address"
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-2.5 py-2 text-zinc-50 text-sm focus:outline-none focus:border-amber-500" />
                </div>
                <div>
                  <label className="text-zinc-400 text-[10px] mb-1 block">Transport mode</label>
                  <select value={form.default_transport_mode || ''}
                    data-testid="default-transport-mode"
                    onChange={e => setForm(f => ({ ...f, default_transport_mode: e.target.value }))}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-2.5 py-2 text-zinc-50 text-sm focus:outline-none focus:border-amber-500">
                    <option value="" className="bg-zinc-900">— Select —</option>
                    {['Road', 'Rail', 'Air', 'Sea', 'Hand-delivered'].map(m => (
                      <option key={m} value={m} className="bg-zinc-900">{m}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-zinc-400 text-[10px] mb-1 block">Transporter name</label>
                  <input value={form.default_transporter_name || ''}
                    data-testid="default-transporter-name"
                    onChange={e => setForm(f => ({ ...f, default_transporter_name: e.target.value }))}
                    placeholder="VRL Logistics, Gati, …"
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-2.5 py-2 text-zinc-50 text-sm focus:outline-none focus:border-amber-500" />
                </div>
                <div>
                  <label className="text-zinc-400 text-[10px] mb-1 block">Vehicle no. (default)</label>
                  <input value={form.default_vehicle_no || ''}
                    data-testid="default-vehicle-no"
                    onChange={e => setForm(f => ({ ...f, default_vehicle_no: e.target.value }))}
                    placeholder="MH09AB1234"
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-2.5 py-2 text-zinc-50 text-sm focus:outline-none focus:border-amber-500" />
                </div>
                <div>
                  <label className="text-zinc-400 text-[10px] mb-1 block">Reference prefix</label>
                  <input value={form.default_reference_prefix || ''}
                    data-testid="default-reference-prefix"
                    onChange={e => setForm(f => ({ ...f, default_reference_prefix: e.target.value }))}
                    placeholder="PO/AT/, REF-"
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-2.5 py-2 text-zinc-50 text-sm focus:outline-none focus:border-amber-500" />
                </div>
                <div className="col-span-2 flex items-center gap-2 pt-1">
                  <input type="checkbox" id="ewb-required"
                    data-testid="default-eway-required"
                    checked={!!form.default_eway_bill_required}
                    onChange={e => setForm(f => ({ ...f, default_eway_bill_required: e.target.checked }))}
                    className="w-4 h-4 accent-amber-500" />
                  <label htmlFor="ewb-required" className="text-zinc-300 text-xs cursor-pointer">E-Way Bill required for this client</label>
                </div>
              </div>
            </div>

            <div className="col-span-2">
              <label className="text-zinc-400 text-xs mb-1.5 block">Notes</label>
              <input value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                placeholder="Optional notes" className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2.5 text-zinc-50 text-sm focus:outline-none focus:border-amber-500" />
            </div>
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="flex-1 py-2.5 rounded-xl border border-zinc-700 text-zinc-400 hover:bg-zinc-800 text-sm transition-colors">Cancel</button>
            <button type="submit" disabled={submitting} data-testid="save-debtor-btn"
              className="flex-1 py-2.5 rounded-xl bg-amber-500 hover:bg-amber-400 text-black font-semibold text-sm disabled:opacity-50 transition-colors">
              {submitting ? 'Saving...' : editing ? 'Update Debtor' : 'Add Debtor'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
